/* eslint-disable camelcase */
import React, { FunctionComponent } from 'react';
import { useRouter } from 'next/router';
import PageProps from '@modules/utilities/pageBuild/types/PageProps';
import { NavigationTypeRaw } from '@modules/utilities/pageBuild/types/NavigationRaw';
import OpenCloudContainer from '@modules/openCloud/page/main/components/OpenCloudContainer';
import RoasOpenCloudContainer from '@modules/openCloud/page/main/components/RoasOpenCloudContainer';
import ArticleContainer from '@modules/article/components/ArticleContainer';
import ReferenceContainer from '@modules/apiReference/page/sharedUI/components/ReferenceContainer';
import ReferenceTypesDataProvider from '@modules/apiReference/page/sharedUI/components/ReferenceTypesDataProvider';
import OpenCloudAPIContent from '@modules/openCloud/utils/interfaces/OpenCloudAPIContent';
import ReleaseNoteContainer from '@modules/article/components/ReleaseNoteContainer';
import { ClassReference } from '@modules/apiReference/page/types';
import { ClassReferenceParent, ReferenceType } from '@modules/client';
import ApiMember, { ApiMemberReferenceProps } from '@modules/apiReference/page/ApiMember';
import LandingPage, { LandingPageData } from '@modules/layout/LandingPage';
import LegacyAPIContainer from '@modules/openCloud/page/main/components/LegacyAPIContainer';
import LessonContainer from '@modules/learning/components/LessonContainer';
import OpenAPIContainer from '@modules/openCloud/openapi/components/OpenAPIDocumentContainer';
import PageNotFound from '@modules/miscellaneous/common/components/error/PageNotFound';
import ReferenceContainerV2 from '@modules/apiReference/page/sharedUI/components/ReferenceContainerV2';
import cloudFragmentRedirectMap from 'configs/cloudFragmentRedirectMap';
import { CLOUD_PAGE_IAV2_OPT_OUT_COOKIE } from '@modules/utilities/constants/cloudPageOptOutCookieConstants';
import { getCookieValueByKey } from '@rbx/core';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';

export interface GenericAppContainerProps {
  data: PageProps;
  isLandingPage: boolean;
}

const GenericAppContainer: FunctionComponent<React.PropsWithChildren<GenericAppContainerProps>> = ({
  data,
  isLandingPage,
}) => {
  const router = useRouter();
  const { enableEngineReferenceRevamp } = useAppConfig();
  if (
    process.env.enableOpenCloudIAV2Pages &&
    getCookieValueByKey(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name) === null
  ) {
    const redirect = cloudFragmentRedirectMap[router.asPath];
    if (redirect && redirect !== router.asPath) {
      router.replace(redirect);
    }
  }

  if (isLandingPage) {
    return <LandingPage data={data as LandingPageData} />;
  }
  const { type, title } = data;
  if (type === NavigationTypeRaw.CloudLegacy) {
    const { content, openGraphMeta } = data;
    return (
      <LegacyAPIContainer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        versions={[(content as any).info.version]}
        content={content}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        title={title ?? (content as any).info.title ?? ''}
        openGraphMeta={openGraphMeta}
      />
    );
  }
  if (type === NavigationTypeRaw.CloudAPI) {
    const { content, openGraphMeta } = data;
    return (
      <OpenCloudContainer
        openGraphMeta={openGraphMeta}
        content={content as OpenCloudAPIContent}
        allVersions={[]}
      />
    );
  }
  if (type === NavigationTypeRaw.CloudAPI2) {
    const { content, openGraphMeta } = data;
    return <RoasOpenCloudContainer openGraphMeta={openGraphMeta} content={content} />;
  }
  if (type === NavigationTypeRaw.CloudFeature) {
    if (
      !process.env.enableOpenCloudIAV2Pages ||
      getCookieValueByKey(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name) !== null
    ) {
      return <PageNotFound />;
    }
    const { content, openGraphMeta } = data;
    return <OpenAPIContainer content={content} openGraphMeta={openGraphMeta} />;
  }
  if (type === NavigationTypeRaw.CloudDomain) {
    if (
      !process.env.enableOpenCloudIAV2Pages ||
      getCookieValueByKey(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name) !== null
    ) {
      return <PageNotFound />;
    }
    const { content, openGraphMeta } = data;
    return <OpenAPIContainer content={content} openGraphMeta={openGraphMeta} />;
  }
  if (type === NavigationTypeRaw.EngineAPI) {
    const { description, apiReference, classReferenceParents, openGraphMeta, referenceTypesData } =
      data;
    if (enableEngineReferenceRevamp) {
      return (
        <ReferenceTypesDataProvider referenceTypesData={referenceTypesData}>
          <ReferenceContainerV2
            title={title ?? ''}
            openGraphMeta={openGraphMeta}
            description={description}
            apiReference={apiReference as ClassReference}
            classReferenceParents={classReferenceParents as ClassReferenceParent[]}
          />
        </ReferenceTypesDataProvider>
      );
    }

    return (
      <ReferenceTypesDataProvider referenceTypesData={referenceTypesData}>
        <ReferenceContainer
          title={title ?? ''}
          openGraphMeta={openGraphMeta}
          description={description}
          apiReference={apiReference as ClassReference}
          classReferenceParents={classReferenceParents as ClassReferenceParent[]}
        />
      </ReferenceTypesDataProvider>
    );
  }
  // SEO optimized API page
  if (type === NavigationTypeRaw.EngineAPIMember) {
    const { memberData, memberType, openGraphMeta, referenceType, referenceTypesData } = data;
    if (referenceType === ReferenceType.ENUM) {
      throw Error("ReferenceType Enum shouldn't have a member");
    }
    const referenceProps = {
      referenceType,
      data: memberData,
      type: memberType,
    } as ApiMemberReferenceProps;

    return (
      <ReferenceTypesDataProvider referenceTypesData={referenceTypesData}>
        <ApiMember openGraphMeta={openGraphMeta} title={title} {...referenceProps} />
      </ReferenceTypesDataProvider>
    );
  }
  if (type === NavigationTypeRaw.Markdown) {
    const { meta, content, headings, openGraphMeta, seoStructuredData, prev, next } = data;
    return (
      <ArticleContainer
        openGraphMeta={openGraphMeta}
        title={title}
        seoStructureData={seoStructuredData}
        banner={meta.banner}
        description={null}
        content={content}
        next={next}
        prev={prev}
        headings={headings}
      />
    );
  }
  if (type === NavigationTypeRaw.ReleaseNote) {
    const { releaseNoteContents, openGraphMeta, releaseNoteTypeList } = data;
    return (
      <ReleaseNoteContainer
        openGraphMeta={openGraphMeta}
        title={releaseNoteContents.title}
        content={releaseNoteContents.content}
        next={releaseNoteContents.next ?? null}
        prev={releaseNoteContents.prev ?? null}
        headings={[]}
        releaseNoteTypeList={releaseNoteTypeList}
      />
    );
  }
  if (type === NavigationTypeRaw.Lesson) {
    const { content, description, headings, next, prev, slugs } = data;
    return (
      <LessonContainer
        content={content}
        title={title}
        description={description}
        headings={headings}
        next={next}
        prev={prev}
        slugs={slugs}
      />
    );
  }

  // NOTE (@christinewang): should we redirect to 404 instead?
  return null;
};

export default GenericAppContainer;
